import {
  AutoSelection,
  Badge,
  BlockStack,
  Box,
  Combobox,
  EmptySearchResult,
  Icon,
  InlineStack,
  Listbox,
  Spinner,
  Text,
  Thumbnail,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import type { PickerItem } from '@/queries/notifications';

export interface ResourcePickerProps {
  label: string;
  placeholder: string;
  items: PickerItem[];
  loading: boolean;
  search: string;
  onSearchChange: (q: string) => void;
  value: string;
  onPick: (item: PickerItem) => void;
  onClear: () => void;
}

export function ResourcePicker({
  label,
  placeholder,
  items,
  loading,
  search,
  onSearchChange,
  value,
  onPick,
  onClear,
}: ResourcePickerProps) {
  const q = search.trim().toLowerCase();
  const filtered = q ? items.filter((i) => i.title.toLowerCase().includes(q)) : items;
  const visible = filtered.slice(0, 100);

  return (
    <Combobox
      activator={
        <Combobox.TextField
          prefix={<Icon source={SearchIcon} />}
          label={label}
          value={search}
          onChange={(v) => {
            onSearchChange(v);
            if (v !== '' && value) onClear();
          }}
          placeholder={placeholder}
          autoComplete="off"
          clearButton
          onClearButtonClick={onClear}
          suffix={
            value ? (
              <Badge tone="success">selected</Badge>
            ) : loading ? (
              <Spinner accessibilityLabel="loading" size="small" />
            ) : (
              <Text as="span" tone="subdued" variant="bodySm">
                {items.length}
              </Text>
            )
          }
        />
      }
    >
      {visible.length > 0 ? (
        <Listbox
          autoSelection={AutoSelection.None}
          onSelect={(val: string) => {
            const item = items.find((i) => i.value === val);
            if (item) onPick(item);
          }}
        >
          {visible.map((item) => (
            <Listbox.Option
              key={item.value}
              value={item.value}
              selected={value === item.value}
              accessibilityLabel={item.title}
            >
              <Box paddingBlock="100" paddingInline="200">
                <InlineStack gap="200" blockAlign="center" wrap={false}>
                  <Thumbnail source={item.thumbnail || ''} alt={item.title} size="small" />
                  <BlockStack gap="050">
                    <Text as="span" variant="bodyMd" truncate>
                      {item.title}
                    </Text>
                    <Text as="span" variant="bodySm" tone="subdued" truncate>
                      {item.value}
                    </Text>
                  </BlockStack>
                </InlineStack>
              </Box>
            </Listbox.Option>
          ))}
          {filtered.length > visible.length ? (
            <Box padding="200">
              <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                عرض أول {visible.length} من {filtered.length} — ابحث لتضيق النتائج
              </Text>
            </Box>
          ) : null}
        </Listbox>
      ) : (
        <EmptySearchResult
          title="مفيش نتائج"
          description={`جرّب كلمة بحث تانية. (المتاح: ${items.length})`}
          withIllustration={false}
        />
      )}
    </Combobox>
  );
}
